import type { StoreConfig } from '../services/backend';
import { COMPANY_EMAIL, CONTACT_PHONE, WHATSAPP_NUMBER } from '../../constants';

const defaultCloudinary = {
  cloudName: 'dmkxyykz9',
  apiKey: '783642512382178',
  apiSecret: '',
};

const DEFAULT_STORE_PHONE = CONTACT_PHONE;
const DEFAULT_STORE_ADDRESS =
  'Rua J 46, Qd 46, Lt 20, Galpão 001, Sala 001\nMansões Paraíso, Aparecida de Goiânia - GO';

const LEGACY_TOPBAR_BACKGROUNDS = new Set(['#0F0F0F', '#1A1A1A', '#111111']);
const LEGACY_TOPBAR_TEXT_COLORS = new Set(['#FAF9F7', '#FFFFFF']);
const DEFAULT_HERO_INTERVAL_MS = 4000;

const normalizeColorValue = (value?: string | null) => (value || '').trim().toUpperCase();
const normalizeHeroInterval = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) && value >= DEFAULT_HERO_INTERVAL_MS
    ? value
    : DEFAULT_HERO_INTERVAL_MS;

export const getDefaultStoreConfig = (): StoreConfig => ({
  name: 'Veraz Cosmeticos',
  description: 'Realcando sua beleza natural todos os dias.',
  colors: {
    primary: '#E6007E',
    secondary: '#D4AF37',
    accent: '#F7F7F7',
  },
  featuredProducts: [],
  bestSellerProducts: [],
  banners: {
    hero: [],
    heroInterval: DEFAULT_HERO_INTERVAL_MS,
    mid: [],
    footer: [],
  },
  catalogBanners: {
    products: '',
    kits: 'https://res.cloudinary.com/dmkxyykz9/image/upload/v1775056794/vfzl2kr0uhlw7orklvgs.webp',
    equipment: '',
  },
  benefitsStrip: {
    enabled: true,
    items: [
      {
        icon: 'Truck',
        title: 'entrega rapida',
        subtitle: 'acompanhe seu pedido do despacho ate a entrega',
      },
      {
        icon: 'CreditCard',
        title: 'parcele sem complicacao',
        subtitle: 'condicoes facilitadas para comprar com mais conforto',
      },
      {
        icon: 'ShieldCheck',
        title: 'compra segura',
        subtitle: 'pagamento protegido e atendimento de confianca',
      },
      {
        icon: 'Heart',
        title: 'favoritos da rotina',
        subtitle: 'curadoria pensada para autocuidado e presenteaveis',
      },
    ],
  },
  educationBlock: {
    enabled: true,
    tag: '#destaque da campanha',
    title: 'produto em',
    highlightedTitle: 'destaque',
    description:
      'Crie uma secao promocional com imagem grande, card rosa e CTA forte para destacar a campanha do momento.',
    backgroundImage: '',
    mobileBackgroundImage: '',
    gradientFrom: '#ffffff',
    gradientTo: '#ffe4f1',
    priceLabel: 'a partir de',
    priceValue: 'R$ 39,90',
    secondaryText: 'fragrancias, kits e autocuidado com a identidade da marca.',
    cardBackgroundColor: '#FF0A7A',
    cardTextColor: '#FFFFFF',
    items: [],
    primaryButton: {
      text: 'eu quero!',
      link: '/produtos',
      backgroundColor: 'transparent',
      textColor: '#FFFFFF',
    },
  },
  topbar: {
    enabled: true,
    animated: false,
    text: 'Garanta agora o seu kit ve favorito!',
    backgroundColor: '#FF0A7A',
    textColor: '#FFFFFF',
    links: [{ label: 'Garanta agora o seu kit ve favorito!', url: '/kits' }],
  },
  buyButton: {
    text: 'Comprar agora',
    icon: 'ShoppingCart',
    showIcon: true,
  },
  integrations: {
    googleAnalytics: '',
    facebookPixel: '',
    cloudinary: { ...defaultCloudinary },
  },
  shipping: {
    enableFreeShipping: false,
    freeShippingThreshold: 0,
    freeShippingRegions: [],
    enableStorePickup: false,
    enableProductShippingCalculator: true,
  },
  ai: {
    geminiApiKey: '',
    googleSearchApiKey: '',
    googleSearchCx: '',
    autoBlogEnabled: false,
    postsPerDay: 0,
    model: '',
  },
  contacts: {
    whatsapp: WHATSAPP_NUMBER,
    email: COMPANY_EMAIL,
    phone: DEFAULT_STORE_PHONE,
    address: DEFAULT_STORE_ADDRESS,
  },
  floatingWhatsapp: {
    enabled: true,
    numberMode: 'default',
    customNumber: '',
    message: 'Ola, vim pelo site da Veraz e gostaria de atendimento.',
    productMessageEnabled: true,
    productMessageTemplate:
      'Ola, vim pelo site da Veraz e gostaria de ajuda com o produto {produto}. Link: {link}',
    showProductHelpText: true,
    productHelpText: 'Precisa de ajuda?',
  },
  payments: {
    enableGatewayPayment: true,
    enableWhatsappPayment: true,
  },
  homeSections: {
    featured: {
      enabled: true,
      title: 'queridinhos da',
      accentText: 've',
      subtitle: '',
      viewAllText: 'ver todos os produtos',
    },
    bestSellers: {
      enabled: true,
      title: 'os mais',
      accentText: 'vendidos',
      subtitle: '',
      viewAllText: 'ver vitrine completa',
      autoMode: 'popular',
    },
    stories: {
      enabled: true,
      title: 'stories da',
      source: 'generated',
      items: [],
      subtitle: 'editoriais, bastidores e conteúdos rápidos da linha Veraz.',
    },
    newsletter: {
      enabled: true,
      title: 'receba dicas e novidades toda semana',
      subtitle: 'no seu e-mail',
      buttonText: 'enviar',
      namePlaceholder: 'Nome',
      phonePlaceholder: '(11) 11111-1111',
      emailPlaceholder: 'E-mail',
      disclaimer: 'Ao enviar, voce concorda em receber comunicacoes da Veraz.',
    },
    social: {
      enabled: true,
      title: 'siga',
      handle: '@verazbrasil',
      subtitle: 'acompanhe lancamentos, bastidores e favoritos da rotina.',
    },
  },
});

export const mergeStoreConfigWithDefaults = (config?: Partial<StoreConfig> | null): StoreConfig => {
  const defaults = getDefaultStoreConfig();
  const defaultShipping = defaults.shipping!;
  const defaultAi = defaults.ai!;
  const defaultPayments = defaults.payments!;
  const defaultFloatingWhatsapp = defaults.floatingWhatsapp!;
  const defaultHomeSections = defaults.homeSections!;
  const defaultCatalogBanners = defaults.catalogBanners || {
    products: '',
    kits: '',
    equipment: '',
  };
  const mergedColors = {
    ...defaults.colors,
    ...(config?.colors || {}),
  };
  const requestedTopbar: Partial<StoreConfig['topbar']> = config?.topbar || {};
  const normalizedTopbarBackground = normalizeColorValue(requestedTopbar.backgroundColor);
  const normalizedTopbarText = normalizeColorValue(requestedTopbar.textColor);
  const shouldSyncLegacyTopbarToBrand =
    !!requestedTopbar.backgroundColor &&
    LEGACY_TOPBAR_BACKGROUNDS.has(normalizedTopbarBackground) &&
    (!requestedTopbar.textColor || LEGACY_TOPBAR_TEXT_COLORS.has(normalizedTopbarText));
  const mergedTopbar = {
    ...defaults.topbar,
    ...requestedTopbar,
    backgroundColor: shouldSyncLegacyTopbarToBrand
      ? mergedColors.primary
      : requestedTopbar.backgroundColor ?? defaults.topbar.backgroundColor,
    textColor: shouldSyncLegacyTopbarToBrand
      ? defaults.topbar.textColor
      : requestedTopbar.textColor ?? defaults.topbar.textColor,
    links: requestedTopbar.links ?? defaults.topbar.links,
  };

  return {
    ...defaults,
    ...config,
    colors: mergedColors,
    featuredProducts: config?.featuredProducts ?? defaults.featuredProducts,
    bestSellerProducts: config?.bestSellerProducts ?? defaults.bestSellerProducts,
    banners: {
      ...defaults.banners,
      ...(config?.banners || {}),
      hero: (config?.banners?.hero ?? defaults.banners.hero).map((banner) => ({
        ...banner,
        showButton: banner.showButton ?? false,
      })),
      heroInterval: normalizeHeroInterval(config?.banners?.heroInterval),
      mid: config?.banners?.mid ?? defaults.banners.mid,
      footer: config?.banners?.footer ?? defaults.banners.footer,
    },
    catalogBanners: {
      products: config?.catalogBanners?.products ?? defaultCatalogBanners.products,
      kits: config?.catalogBanners?.kits ?? defaultCatalogBanners.kits,
      equipment: config?.catalogBanners?.equipment ?? defaultCatalogBanners.equipment,
    },
    benefitsStrip: {
      ...defaults.benefitsStrip,
      ...(config?.benefitsStrip || {}),
      items: config?.benefitsStrip?.items ?? defaults.benefitsStrip.items,
    },
    educationBlock: {
      ...defaults.educationBlock,
      ...(config?.educationBlock || {}),
      items: config?.educationBlock?.items ?? defaults.educationBlock.items,
      primaryButton: {
        ...defaults.educationBlock.primaryButton,
        ...(config?.educationBlock?.primaryButton || {}),
      },
    },
    topbar: mergedTopbar,
    buyButton: {
      ...defaults.buyButton,
      ...(config?.buyButton || {}),
    },
    integrations: {
      ...defaults.integrations,
      ...(config?.integrations || {}),
      cloudinary: {
        ...defaultCloudinary,
        ...(config?.integrations?.cloudinary || {}),
      },
      mercadopago: {
        publicKey: '',
        accessToken: '',
        ...(config?.integrations?.mercadopago || {}),
      },
      melhorEnvio: {
        clientId: '',
        clientSecret: '',
        accessToken: '',
        refreshToken: '',
        tokenExpiresAt: 0,
        sandbox: false,
        ...(config?.integrations?.melhorEnvio || {}),
      },
      bling: {
        clientId: '',
        clientSecret: '',
        apiKey: '',
        accessToken: '',
        refreshToken: '',
        expiresIn: '',
        ...(config?.integrations?.bling || {}),
      },
      googleMerchant: {
        lastSync: '',
        ...(config?.integrations?.googleMerchant || {}),
      },
    },
    shipping: {
      enableFreeShipping:
        config?.shipping?.enableFreeShipping ?? defaultShipping.enableFreeShipping,
      freeShippingThreshold:
        config?.shipping?.freeShippingThreshold ?? defaultShipping.freeShippingThreshold,
      freeShippingRegions:
        config?.shipping?.freeShippingRegions ?? defaultShipping.freeShippingRegions,
      enableStorePickup: config?.shipping?.enableStorePickup ?? defaultShipping.enableStorePickup,
      enableProductShippingCalculator:
        config?.shipping?.enableProductShippingCalculator ??
        defaultShipping.enableProductShippingCalculator,
    },
    ai: {
      geminiApiKey: config?.ai?.geminiApiKey ?? defaultAi.geminiApiKey,
      googleSearchApiKey: config?.ai?.googleSearchApiKey ?? defaultAi.googleSearchApiKey,
      googleSearchCx: config?.ai?.googleSearchCx ?? defaultAi.googleSearchCx,
      autoBlogEnabled: config?.ai?.autoBlogEnabled ?? defaultAi.autoBlogEnabled,
      postsPerDay: config?.ai?.postsPerDay ?? defaultAi.postsPerDay,
      model: config?.ai?.model ?? defaultAi.model,
    },
    contacts: {
      whatsapp: config?.contacts?.whatsapp?.trim() || defaults.contacts.whatsapp,
      email: config?.contacts?.email?.trim() || defaults.contacts.email,
      phone: config?.contacts?.phone?.trim() || defaults.contacts.phone,
      address: config?.contacts?.address?.trim() || defaults.contacts.address,
    },
    floatingWhatsapp: {
      ...defaultFloatingWhatsapp,
      ...(config?.floatingWhatsapp || {}),
      numberMode:
        config?.floatingWhatsapp?.numberMode === 'custom'
          ? 'custom'
          : defaultFloatingWhatsapp.numberMode,
      customNumber:
        config?.floatingWhatsapp?.customNumber ?? defaultFloatingWhatsapp.customNumber,
      message: config?.floatingWhatsapp?.message ?? defaultFloatingWhatsapp.message,
      productMessageTemplate:
        config?.floatingWhatsapp?.productMessageTemplate ??
        defaultFloatingWhatsapp.productMessageTemplate,
      productHelpText:
        config?.floatingWhatsapp?.productHelpText ?? defaultFloatingWhatsapp.productHelpText,
    },
    payments: {
      enableGatewayPayment:
        config?.payments?.enableGatewayPayment ?? defaultPayments.enableGatewayPayment,
      enableWhatsappPayment:
        config?.payments?.enableWhatsappPayment ?? defaultPayments.enableWhatsappPayment,
    },
    homeSections: {
      ...defaultHomeSections,
      ...(config?.homeSections || {}),
      featured: {
        ...defaultHomeSections.featured,
        ...(config?.homeSections?.featured || {}),
      },
      bestSellers: {
        ...defaultHomeSections.bestSellers,
        ...(config?.homeSections?.bestSellers || {}),
      },
      stories: {
        ...defaultHomeSections.stories,
        ...(config?.homeSections?.stories || {}),
      },
      newsletter: {
        ...defaultHomeSections.newsletter,
        ...(config?.homeSections?.newsletter || {}),
      },
      social: {
        ...defaultHomeSections.social,
        ...(config?.homeSections?.social || {}),
      },
    },
  };
};
